(function () {
  'use strict';
  if (customElements.get('rapid-ndis-request')) return;

  class RapidNdisRequest extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        window.addEventListener('pageshow', this.onPageShow);
        return;
      }
      this.initialized = true;
      this.form = this.querySelector('form');
      this.bodyField = this.querySelector('[data-ndis-products]');
      this.submitButton = this.querySelector('[data-ndis-submit]');
      this.prefillStatus = this.querySelector('[data-ndis-prefill-status]');
      this.submitStatus = this.querySelector('[data-ndis-submit-status]');
      this.prefillGeneration = 0;
      this.userEditedBody = false;
      this.pendingSubmission = null;
      this.onPageShow = this.resetSubmission.bind(this);
      window.addEventListener('pageshow', this.onPageShow);

      const feedback = this.querySelector('[data-ndis-server-feedback]');
      if (feedback && (!document.activeElement || document.activeElement === document.body)) {
        feedback.focus({ preventScroll: false });
      }
      if (!this.form || !this.bodyField || this.querySelector('[data-ndis-success]')) return;

      this.initialBody = this.bodyField.value;
      this.bodyField.addEventListener('input', () => {
        this.userEditedBody = true;
        if (this.prefillController) {
          this.cancelPrefill();
          this.showStatus(this.prefillStatus, this.dataset.prefillKept);
        }
      });
      this.form.addEventListener('invalid', event => this.showFieldError(event.target), true);
      this.form.addEventListener('input', event => this.clearFieldError(event.target));
      this.form.addEventListener('change', event => this.clearFieldError(event.target));
      this.form.addEventListener('submit', event => this.trackSubmission(event));

      const copyButton = this.querySelector('[data-ndis-copy-email]');
      if (copyButton) {
        copyButton.hidden = false;
        copyButton.addEventListener('click', () => this.copyEmail());
      }
      this.prefillProduct();
    }

    disconnectedCallback() {
      this.cancelPrefill();
      if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
      this.resetSubmission();
    }

    showStatus(target, message) {
      if (!target) return;
      target.textContent = message || '';
      target.hidden = !message;
    }

    showFieldError(control) {
      if (!control || !control.closest) return;
      const group = control.closest('.rapid-ndis-request__field');
      const error = group && group.querySelector('[data-ndis-field-error]');
      if (!error) return;
      control.setAttribute('aria-invalid', 'true');
      error.textContent = control.validationMessage;
      error.hidden = false;
    }

    clearFieldError(control) {
      if (!control || !control.validity || !control.validity.valid || !control.closest) return;
      const group = control.closest('.rapid-ndis-request__field');
      const error = group && group.querySelector('[data-ndis-field-error]');
      control.removeAttribute('aria-invalid');
      if (error) { error.textContent = ''; error.hidden = true; }
    }

    copyEmail() {
      const source = this.querySelector('[data-ndis-invoice-email]');
      const destination = this.querySelector('[data-ndis-client-email]');
      if (!source || !destination) return;
      if (!source.reportValidity()) { source.focus(); return; }
      destination.value = source.value;
      destination.dispatchEvent(new Event('input', { bubbles: true }));
      destination.dispatchEvent(new Event('change', { bubbles: true }));
      this.showStatus(this.querySelector('[data-ndis-copy-status]'), this.dataset.emailCopied);
    }

    trackSubmission(event) {
      // Shopify's captcha can cancel this event and continue its own native submission.
      // Wait until propagation finishes before showing a committed sending state.
      if (this.pendingSubmission && !this.pendingSubmission.event.defaultPrevented) {
        event.preventDefault();
        return;
      }
      const attempt = { event: event };
      this.pendingSubmission = attempt;
      setTimeout(() => {
        if (this.pendingSubmission !== attempt) return;
        if (event.defaultPrevented) { this.resetSubmission(); return; }
        this.cancelPrefill();
        this.form.setAttribute('aria-busy', 'true');
        if (this.submitButton) this.submitButton.disabled = true;
        this.showStatus(this.submitStatus, this.dataset.sending);
        this.submissionTimer = setTimeout(() => {
          if (this.pendingSubmission !== attempt) return;
          this.resetSubmission();
          this.showStatus(this.submitStatus, this.dataset.sendDelayed);
        }, 30000);
      }, 0);
    }

    resetSubmission() {
      clearTimeout(this.submissionTimer);
      this.pendingSubmission = null;
      if (this.form) this.form.removeAttribute('aria-busy');
      if (this.submitButton) this.submitButton.disabled = false;
      this.showStatus(this.submitStatus, '');
    }

    cancelPrefill() {
      this.prefillGeneration += 1;
      if (this.prefillController) this.prefillController.abort();
      this.prefillController = null;
      clearTimeout(this.prefillTimer);
    }

    async prefillProduct() {
      if (this.bodyField.dataset.returnedBody === 'true') return;
      const params = new URLSearchParams(window.location.search);
      if (!params.has('product') && !params.has('variant') && !params.has('quantity')) return;
      const handle = params.get('product') || '';
      const variantId = params.get('variant') || '';
      const quantityText = params.get('quantity') || '1';
      if (!/^[a-z0-9][a-z0-9-]{0,199}$/.test(handle) || !/^[1-9]\d{0,19}$/.test(variantId) || !/^[1-9]\d{0,3}$/.test(quantityText) ||
          params.getAll('product').length !== 1 || params.getAll('variant').length !== 1 || params.getAll('quantity').length > 1) {
        this.showStatus(this.prefillStatus, this.dataset.prefillError);
        return;
      }
      let root;
      try {
        root = new URL(this.dataset.rootUrl || '/', window.location.origin);
        if (root.origin !== window.location.origin || !root.pathname.endsWith('/') || root.search || root.hash) throw new Error('Invalid storefront root');
      } catch (_) {
        this.showStatus(this.prefillStatus, this.dataset.prefillError);
        return;
      }
      const endpoint = new URL(root.pathname + 'products/' + encodeURIComponent(handle) + '.js', root.origin);
      const controller = new AbortController();
      this.prefillController = controller;
      const generation = ++this.prefillGeneration;
      let timedOut = false;
      this.showStatus(this.prefillStatus, this.dataset.prefillLoading);
      this.prefillTimer = setTimeout(() => { timedOut = true; controller.abort(); }, 10000);
      try {
        const response = await fetch(endpoint.href, {
          method: 'GET', credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller.signal
        });
        if (!response.ok) throw new Error('Product unavailable');
        const product = await response.json();
        if (controller.signal.aborted || timedOut) throw new Error('Product request stopped');
        if (!product || product.handle !== handle || typeof product.title !== 'string' || !Array.isArray(product.variants)) throw new Error('Invalid product');
        const variant = product.variants.find(item => item && String(item.id) === variantId);
        if (!variant || typeof variant.title !== 'string') throw new Error('Variant unavailable');
        if (!this.isConnected || generation !== this.prefillGeneration) return;
        if (this.userEditedBody || this.bodyField.value !== this.initialBody) {
          this.showStatus(this.prefillStatus, this.dataset.prefillKept);
          return;
        }
        const productUrl = new URL(root.pathname + 'products/' + encodeURIComponent(handle), root.origin);
        productUrl.searchParams.set('variant', variantId);
        const text = value => String(value || '').replace(/[\r\n]+/g, ' ').slice(0, 600);
        const title = text(product.title);
        const variantTitle = variant.title === 'Default Title' ? '' : text(variant.title);
        const lines = [title + (variantTitle ? ' — ' + variantTitle : ''), this.dataset.quantityLabel + ': ' + Number(quantityText)];
        if (variant.sku) lines.push(this.dataset.skuLabel + ': ' + text(variant.sku));
        lines.push(this.dataset.productLinkLabel + ': ' + productUrl.href);
        this.bodyField.value = lines.join('\n');
        this.clearFieldError(this.bodyField);
        this.showStatus(this.prefillStatus, this.dataset.prefillSuccess);
      } catch (_) {
        if (this.isConnected && generation === this.prefillGeneration && !this.userEditedBody) {
          this.showStatus(this.prefillStatus, this.dataset.prefillError);
        }
      } finally {
        if (generation === this.prefillGeneration) {
          clearTimeout(this.prefillTimer);
          this.prefillController = null;
        }
      }
    }
  }

  customElements.define('rapid-ndis-request', RapidNdisRequest);
})();
